import { loadStripe } from "@stripe/stripe-js";

/**
 * Stripe publishable key selection, matched to the database the request will hit.
 *
 * The backend picks its Stripe SECRET key per request from the `X-App-Version:
 * development-app` header (see backend utilities/stripe.js): dev header => test
 * client, otherwise live. The browser has to make the SAME choice for its
 * PUBLISHABLE key, because a client_secret minted by one mode cannot be confirmed
 * by the other — Stripe rejects it with "No such payment_intent" / "client_secret
 * does not match any associated payment intent... ensure the publishable key
 * matches".
 *
 * That mismatch is reachable in production: the mobile app hands off to
 * dancelocale.com/tickets?...&devMode=true, which makes the API client send the dev
 * header (so the intent is created in TEST) while the production bundle only ever
 * had a live publishable key. Guest ticket checkout from a dev-database build could
 * not complete at all.
 *
 * Detection mirrors src/api/client.js exactly. If the two ever diverge, this bug
 * comes straight back — change them together.
 *
 * "Option A" fallback, same as the backend's: when no test key is configured, fall
 * back to the single flat key. So this file is inert until
 * REACT_APP_STRIPE_PUBLISHABLE_KEY_TEST is actually set in the environment.
 */
function shouldUseTestKey() {
    if (typeof window === "undefined") return false;
    const IS_LOCALHOST = window.location.hostname === "localhost";
    const urlParams = new URLSearchParams(window.location.search);
    const devModeFromUrl = urlParams.get("devMode") === "true";
    const devModeFromStorage = localStorage.getItem("dance-locale-dev-mode") === "true";
    return IS_LOCALHOST || devModeFromUrl || devModeFromStorage;
}

// Stripe's own guidance is to call loadStripe once per page load, so the promise is
// memoized. Resolved on FIRST USE rather than at module import: the mobile-handoff
// entry pages (ticketing/CheckoutStart, booking/BookingPayment) write the
// dance-locale-dev-mode flag and then SPA-navigate to the payment page, so a key
// chosen at import time could predate the signal that decides it.
let stripePromise = null;

export function getStripePromise() {
    if (!stripePromise) {
        const liveKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
        const testKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_TEST;
        stripePromise = loadStripe(shouldUseTestKey() ? testKey || liveKey : liveKey);
    }
    return stripePromise;
}

export default getStripePromise;
